/**
 * RichTextEditor — mini editor HTML (contentEditable) con toolbar essenziale:
 * grassetto, corsivo, sottolineato, elenco puntato/numerato.
 *
 * Leggero e senza dipendenze: usa `document.execCommand` (deprecato ma ancora
 * supportato da tutti i browser, adeguato per un editor inline semplice).
 *
 * Non controllato: il contenuto vive nel DOM del div editabile; `onChange`
 * emette l'HTML corrente ad ogni input. Per svuotarlo, il parent rimonta il
 * componente cambiando la `key`.
 *
 * Con `insertables` la toolbar guadagna DUE pulsanti che scrivono al punto del
 * cursore, sulla stessa lista di voci: il primo il solo valore, il secondo il
 * valore preceduto dalla sua etichetta e seguito da un a capo — `Titolo:
 * [$title$]`, la forma di chi compone una mail di riepilogo. I segnaposto
 * `[$campo$]` del builder automazioni sono il primo uso.
 * È l'unica via d'ingresso del componente: non essendo controllato, non c'è un
 * `value` da cui far entrare contenuto, e un handle imperativo esporrebbe molto
 * più di quanto serva.
 *
 * IMPORTANTE: l'HTML prodotto è ora persistito (commenti) e ripresentato ad
 * altri utenti via dangerouslySetInnerHTML, quindi DEVE essere sanificato
 * (whitelist tag) lato backend in scrittura/lettura e/o lato client al render,
 * per evitare stored XSS.
 */

import { Fragment, useEffect, useRef, useState, type MouseEvent, type KeyboardEvent } from 'react';
import { useTranslation } from 'react-i18next';
import Popover from '@mui/material/Popover';
import TextField from '@mui/material/TextField';
import Button from '@mui/material/Button';
import FormatBoldIcon from '@mui/icons-material/FormatBold';
import FormatItalicIcon from '@mui/icons-material/FormatItalic';
import FormatUnderlinedIcon from '@mui/icons-material/FormatUnderlined';
import TitleIcon from '@mui/icons-material/Title';
import FormatColorTextIcon from '@mui/icons-material/FormatColorText';
import { ColorPickerPopover } from './ColorPicker';
import FormatListBulletedIcon from '@mui/icons-material/FormatListBulleted';
import FormatListNumberedIcon from '@mui/icons-material/FormatListNumbered';
import FormatClearIcon from '@mui/icons-material/FormatClear';
import FormatQuoteIcon from '@mui/icons-material/FormatQuote';
import InsertLinkIcon from '@mui/icons-material/InsertLink';
import DataObjectIcon from '@mui/icons-material/DataObject';
import LabelIcon from '@mui/icons-material/Label';

/** Escape minimale per inserire un URL come testo/attributo in modo sicuro. */
function escapeHtml(s: string): string {
  return s
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;');
}

export interface MentionItem {
  /** Identificatore inserito nel token [~value]: screenName dell'utente Liferay. */
  value: string;
  /** Etichetta mostrata (nome completo) nel dropdown e nella chip. */
  label: string;
}

/**
 * Voce inseribile al cursore da un menu della toolbar: `value` è il testo che finisce
 * nell'editor, `label` come lo si chiama nel menu. I due non coincidono quasi mai — un
 * segnaposto si inserisce come `[$firstName$]` e si sceglie come "Nome".
 *
 * `label` non è più soltanto il nome nel menu: il secondo pulsante della toolbar la scrive
 * NEL CONTENUTO, davanti al valore, nella forma `Nome: [$firstName$]`. Chi compone le voci la
 * scelga quindi leggibile in una frase, non solo in un elenco — un'etichetta che spiega la voce
 * a chi la sceglie (per esempio un "(con orario)" che distingue due forme dello stesso campo)
 * finisce tale e quale nel testo.
 */
export interface InsertableItem {
  value: string;
  label: string;
}

interface RichTextEditorProps {
  onChange: (html: string) => void;
  placeholder?: string;
  autoFocus?: boolean;
  /**
   * Voci che un menu della toolbar inserisce al punto del cursore. Assenti o vuote, il
   * pulsante non compare: un editor che non ha nulla da inserire non deve mostrare uno
   * strumento che apre un menu vuoto.
   *
   * Deliberatamente GENERICO: l'editor sa inserire testo al cursore e non che cosa quel testo
   * significhi. Chi lo monta decide se sono segnaposto di una mail, sigle o altro — è la stessa
   * separazione per cui `mentionSearch` riceve una funzione di ricerca e non i contatti del CRM.
   */
  insertables?: InsertableItem[];
  /**
   * Etichetta del pulsante che apre le voci inseribili. Default: `rte.insert`.
   *
   * Vale per il primo dei due pulsanti, quello che inserisce il solo valore. Il secondo — che
   * scrive `etichetta: valore` — tiene la propria etichetta generica (`rte.insertLabeled`): il
   * modo in cui compone è dell'editor, non di chi lo monta, quindi la sua descrizione non è
   * qualcosa che il chiamante debba poter riscrivere.
   */
  insertablesLabel?: string;
  /** Se fornito, digitando "@" si apre la lista di menzioni filtrate sulla
   *  query (testo dopo la chiocciola). Risolvere coi contatti del CRM. */
  mentionSearch?: (query: string) => Promise<MentionItem[]>;
  /** HTML iniettato una volta al mount (edit di un contenuto esistente). Le
   *  menzioni vanno passate già come chip (<span class="mention" data-mention=…
   *  contenteditable="false">@Nome</span>): serializeContent le riconverte in
   *  token [~screenName] al salvataggio. Il parent rimonta via `key` per cambiarlo. */
  initialHtml?: string;
}

// `sep: true` aggiunge una linea verticale di separazione DOPO l'icona.
// Apice e pedice RIMOSSI: nessuno li usa in un commento o in una nota di CRM, e occupavano due
// posti su una toolbar che sfondava i contenitori più stretti. Rimosse anche le chiavi i18n
// `rte.superscript`/`rte.subscript`, a differenza di quanto resta per le immagini qui sotto: là
// il bottone è commentato e tornerà, qui la scelta è presa.
const TOOLS = [
  { cmd: 'bold', Icon: FormatBoldIcon, labelKey: 'rte.bold', sep: false },
  { cmd: 'italic', Icon: FormatItalicIcon, labelKey: 'rte.italic', sep: false },
  { cmd: 'underline', Icon: FormatUnderlinedIcon, labelKey: 'rte.underline', sep: true },
  { cmd: 'insertUnorderedList', Icon: FormatListBulletedIcon, labelKey: 'rte.bulletList', sep: false },
  { cmd: 'insertOrderedList', Icon: FormatListNumberedIcon, labelKey: 'rte.numberedList', sep: true },
] as const;

/**
 * Livelli intestazione (formatBlock), in una TENDINA e non sei pulsanti in fila: erano il gruppo
 * più ingombrante della toolbar e il meno usato per clic, e sei caselle "H1".."H6" chiedono di
 * leggerle una per una. MUI non ha icone per i livelli, quindi le voci restano testuali — ma
 * dentro un menu, dove il testo è a casa sua.
 */
const HEADINGS = [1, 2, 3, 4, 5, 6] as const;

export function RichTextEditor({ onChange, placeholder, autoFocus, mentionSearch, initialHtml, insertables, insertablesLabel }: RichTextEditorProps) {
  const { t } = useTranslation();
  const ref = useRef<HTMLDivElement>(null);
  // Selezione salvata mentre il focus è sull'input URL del popover, e mentre è aperto il menu
  // delle voci inseribili: stesso bisogno — sapere dove scrivere quando si torna — e i due
  // popover non sono mai aperti insieme, quindi un ref solo basta.
  const savedRange = useRef<Range | null>(null);
  const [urlEditor, setUrlEditor] = useState<{ kind: 'link' | 'image'; anchorEl: HTMLElement } | null>(null);
  const [urlValue, setUrlValue] = useState('');
  const [insertablesAnchorEl, setInsertablesAnchorEl] = useState<HTMLElement | null>(null);
  // Quale dei due pulsanti ha aperto il menu delle voci inseribili. Sta nello stato e non in due
  // menu distinti perché la lista è la stessa: cambia solo cosa si scrive quando si sceglie, e due
  // Popover con le stesse voci sarebbero due posti in cui ricordarsi di aggiungere una voce.
  const [insertablesLabeled, setInsertablesLabeled] = useState(false);
  const [headingsAnchorEl, setHeadingsAnchorEl] = useState<HTMLElement | null>(null);
  const [colorAnchorEl, setColorAnchorEl] = useState<HTMLElement | null>(null);
  const [textColor, setTextColor] = useState('#000000');

  // In sola lettura il contenuto non dev'essere digitabile. Chi ospita l'editor la dichiara
  // avvolgendolo in un `<fieldset disabled>` — la convenzione dei form del progetto — che spegne
  // da sé i pulsanti della toolbar, che sono <button> veri, ma NON questo div: `contenteditable`
  // un controllo di form non è, e il fieldset non lo tocca. Senza questa lettura il corpo resta
  // scrivibile proprio dove tutto il resto è spento.
  // Si guarda il fieldset invece di farsi passare una prop perché chi monta l'editor la sola
  // lettura l'ha già dichiarata una volta: una seconda dichiarazione sarebbe una verità in più da
  // tenere allineata, ed è la ragione per cui `ActionConfigProps` un flag di sola lettura non ce
  // l'ha di proposito.
  const [readOnly, setReadOnly] = useState(false);

  // ── Menzioni (@) ──────────────────────────────────────────────────────
  const [mention, setMention] = useState<{ top: number; left: number } | null>(null);
  const [mentionItems, setMentionItems] = useState<MentionItem[]>([]);
  const [mentionIndex, setMentionIndex] = useState(0);
  // requestId per scartare risposte di ricerche superate (race su digitazione).
  const mentionReq = useRef(0);

  /**
   * Se il caret è in un contesto "@parola" (chiocciola a inizio testo o dopo
   * uno spazio, senza spazi nel mezzo), ritorna la query e il Range che copre
   * "@query" (per sostituirlo con la menzione). Altrimenti null.
   */
  function getMentionContext(): { query: string; range: Range } | null {
    const sel = window.getSelection();
    if (!sel || sel.rangeCount === 0 || !sel.isCollapsed) return null;
    const node = sel.anchorNode;
    if (!node || node.nodeType !== Node.TEXT_NODE) return null;
    const offset = sel.anchorOffset;
    const before = (node.textContent ?? '').slice(0, offset);
    const m = before.match(/(?:^|\s)@([\p{L}\p{N}'.-]*)$/u);
    if (!m) return null;
    const query = m[1];
    const atIndex = offset - (query.length + 1);
    const range = document.createRange();
    range.setStart(node, atIndex);
    range.setEnd(node, offset);
    return { query, range };
  }

  function closeMention() {
    setMention(null);
    setMentionItems([]);
    setMentionIndex(0);
  }

  function updateMention() {
    if (!mentionSearch) return;
    const ctx = getMentionContext();
    if (!ctx) {
      closeMention();
      return;
    }
    const rect = ctx.range.getBoundingClientRect();
    setMention({ top: rect.bottom, left: rect.left });
    setMentionIndex(0);
    const rid = ++mentionReq.current;
    mentionSearch(ctx.query)
      .then((items) => {
        if (rid === mentionReq.current) setMentionItems(items);
      })
      .catch(() => {
        if (rid === mentionReq.current) setMentionItems([]);
      });
  }

  function selectMention(item: MentionItem) {
    const ctx = getMentionContext();
    closeMention();
    if (!ctx) return;
    // Sostituisce "@query" con uno span menzione non editabile + spazio.
    ctx.range.deleteContents();
    const span = document.createElement('span');
    span.className = 'mention';
    span.setAttribute('data-mention', item.value);
    span.setAttribute('contenteditable', 'false');
    span.textContent = `@${item.label}`;
    ctx.range.insertNode(span);
    const space = document.createTextNode(' ');
    span.after(space);
    const sel = window.getSelection();
    const after = document.createRange();
    after.setStartAfter(space);
    after.collapse(true);
    sel?.removeAllRanges();
    sel?.addRange(after);
    ref.current?.focus();
    emit();
  }

  function handleEditorKeyDown(e: KeyboardEvent<HTMLDivElement>) {
    if (!mention || mentionItems.length === 0) return;
    if (e.key === 'ArrowDown') {
      e.preventDefault();
      setMentionIndex((i) => (i + 1) % mentionItems.length);
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      setMentionIndex((i) => (i - 1 + mentionItems.length) % mentionItems.length);
    } else if (e.key === 'Enter') {
      e.preventDefault();
      selectMention(mentionItems[mentionIndex]);
    } else if (e.key === 'Escape') {
      e.preventDefault();
      closeMention();
    }
  }

  // SENZA array di dipendenze di proposito: il `disabled` del fieldset cambia per un render del
  // padre, non per un cambio di props di questo componente, e un effetto con dipendenze non se ne
  // accorgerebbe — la modale del builder passa da consultazione a modifica senza rimontare
  // l'editor. Lo stato si riscrive solo quando il valore cambia davvero, quindi non innesca un
  // ciclo di render.
  useEffect(() => {
    const disabled = Boolean(ref.current?.closest('fieldset')?.disabled);

    setReadOnly((current) => (current === disabled ? current : disabled));
  });

  useEffect(() => {
    // Pre-popolamento (edit/rispondi): inietta l'HTML iniziale una volta al mount
    // (il parent rimonta via `key` per cambiarlo) e porta il cursore in fondo, così
    // si scrive DOPO il contenuto (es. sotto la citazione). Le chip menzione restano
    // editabili e serializeContent le riconverte in [~screenName] al salvataggio.
    const root = ref.current;
    if (initialHtml && root) {
      root.innerHTML = initialHtml;
      const range = document.createRange();
      range.selectNodeContents(root);
      range.collapse(false);
      const sel = window.getSelection();
      sel?.removeAllRanges();
      sel?.addRange(range);
    }
    if (autoFocus) root?.focus();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  function emit() {
    onChange(serializeContent());
  }

  // Serializza il contenuto convertendo le chip menzione
  // (<span class="mention" data-mention="screenName">@Nome</span>) nel token
  // parseabile [~screenName]: l'HTML salvato è così automatizzabile/verificabile lato
  // server, indipendente dal nome visualizzato. La risoluzione a @Nome avviene
  // in lettura (client). La chip resta nel DOM dell'editor (solo UX).
  function serializeContent(): string {
    const root = ref.current;
    if (!root) return '';
    const clone = root.cloneNode(true) as HTMLElement;
    clone.querySelectorAll('span.mention[data-mention]').forEach((el) => {
      el.replaceWith(document.createTextNode(`[~${el.getAttribute('data-mention')}]`));
    });
    // Gli a capo "veri" qui sono tag (<br>/<div>/<p>): eventuali \n grezzi nei
    // text node (tipicamente da incolla di testo già mandato a capo) non sono
    // visibili a video (white-space: normal li collassa) ma verrebbero salvati
    // nel body, spezzando frasi e ricerca. Li normalizziamo a spazio.
    return clone.innerHTML.replace(/\r?\n/g, ' ');
  }

  function runCommand(e: MouseEvent, cmd: string, value?: string) {
    // preventDefault: tiene la selezione/focus nell'area editabile, altrimenti
    // il click sul bottone della toolbar la perderebbe e execCommand non agirebbe.
    e.preventDefault();
    // styleWithCSS=false: bold/italic/underline emettono tag (<b>/<i>/<u>)
    // invece di <span style="...">, così la sanificazione allowlist non perde
    // la formattazione.
    document.execCommand('styleWithCSS', false, 'false');
    document.execCommand(cmd, false, value);
    ref.current?.focus();
    emit();
  }

  // Cancella la formattazione: removeFormat (inline: bold/italic/.../apice/pedice)
  // + reset del blocco a paragrafo (toglie le intestazioni). Le liste, essendo
  // toggle, non vengono rimosse.
  function clearFormatting(e: MouseEvent) {
    e.preventDefault();
    document.execCommand('removeFormat', false);
    document.execCommand('formatBlock', false, 'p');
    ref.current?.focus();
    emit();
  }

  // Link/immagine: apre un input inline (popover) ancorato al bottone. La
  // selezione del contentEditable viene salvata ora (mousedown, prima che il
  // focus passi all'input) e ripristinata alla conferma, così l'inserimento
  // avviene nel punto giusto.
  function openUrlEditor(kind: 'link' | 'image', e: MouseEvent) {
    e.preventDefault();
    saveRange();
    setUrlValue('');
    setUrlEditor({ kind, anchorEl: e.currentTarget as HTMLElement });
  }

  // Dove stava il cursore, da ricordare finché un popover è aperto. I tre strumenti che ne aprono
  // uno (URL, voci inseribili, titoli) passano di qui invece di rileggere la selezione ciascuno a
  // modo suo: tre letture della stessa cosa sono tre modi di sbagliarla in modo diverso.
  function saveRange() {
    const sel = window.getSelection();

    savedRange.current = sel && sel.rangeCount ? sel.getRangeAt(0).cloneRange() : null;
  }

  // Il cursore torna dov'era prima che il popover si aprisse. Senza, `execCommand` agirebbe sulla
  // selezione corrente — che dopo un giro nel popover può essere nessuna.
  function restoreRange() {
    if (!savedRange.current) return;

    const s = window.getSelection();

    s?.removeAllRanges();
    s?.addRange(savedRange.current);
  }

  // Titoli: si apre su mousedown con preventDefault come gli altri strumenti, e la selezione si
  // salva ORA — il Popover viene montato dopo, e il blocco da trasformare è quello in cui il
  // cursore stava prima del clic.
  function openHeadings(e: MouseEvent) {
    e.preventDefault();
    saveRange();
    setHeadingsAnchorEl(e.currentTarget as HTMLElement);
  }

  function applyHeading(level: number) {
    setHeadingsAnchorEl(null);
    ref.current?.focus();
    restoreRange();
    // styleWithCSS=false per la stessa ragione di runCommand: tag e non attributi di stile, o la
    // sanificazione allowlist perderebbe la formattazione.
    document.execCommand('styleWithCSS', false, 'false');
    document.execCommand('formatBlock', false, `h${level}`);
    emit();
  }

  // Colore del testo: il selettore dell'applicazione (ColorPickerPopover), che riparte dall'ultimo
  // colore usato. Si salva dove stava il cursore, come per i titoli.
  function openColorPicker(e: MouseEvent) {
    e.preventDefault();
    saveRange();
    setColorAnchorEl(e.currentTarget as HTMLElement);
  }

  // styleWithCSS=false anche qui: il colore esce come <font color>, un tag, e non come attributo
  // style che la sanificazione toglierebbe.
  function applyColor(hex: string) {
    setColorAnchorEl(null);
    setTextColor(hex);
    ref.current?.focus();
    restoreRange();
    document.execCommand('styleWithCSS', false, 'false');
    document.execCommand('foreColor', false, hex);
    emit();
  }

  // Voci inseribili: stesso giro dei titoli — salvare dove si era, riaprirci il cursore, agire.
  // `labeled` viaggia nello stato e non come argomento dell'inserimento: al clic sulla voce il
  // pulsante che ha aperto il menu non è più di mezzo, e la lista non sa da dove la si è aperta.
  function openInsertables(e: MouseEvent, labeled: boolean) {
    e.preventDefault();
    saveRange();
    setInsertablesLabeled(labeled);
    setInsertablesAnchorEl(e.currentTarget as HTMLElement);
  }

  // insertText e non insertHTML: la voce è testo e come testo dev'essere trattata, o una che
  // contenesse "<" aprirebbe un tag nel corpo della mail. È anche l'unico modo perché il valore
  // resti modificabile a mano come tutto il resto.
  //
  // In modalità etichettata l'editor compone `etichetta: valore` e chiude con un'interruzione di
  // riga: chi scrive una mail di riepilogo sceglie tre voci di fila e ha tre righe, senza premere
  // Invio fra una e l'altra. L'a capo è un `<br>` con un insertHTML a parte e NON un `\n` dentro
  // insertText: un `\n` in un contentEditable dipende dal browser — c'è chi lo rende interruzione e
  // chi lo scarta in silenzio, e le voci si troverebbero incollate sulla stessa riga senza che
  // niente segnali un errore. Il `<br>` lo scriviamo noi e non contiene dato altrui, quindi la
  // ragione per cui il valore passa da insertText resta intatta.
  function insertItem(item: InsertableItem) {
    setInsertablesAnchorEl(null);
    ref.current?.focus();
    restoreRange();

    document.execCommand(
      'insertText',
      false,
      insertablesLabeled ? `${item.label}: ${item.value}` : item.value,
    );

    if (insertablesLabeled) document.execCommand('insertHTML', false, '<br>');

    emit();
  }

  function confirmUrl() {
    const url = urlValue.trim();
    const kind = urlEditor?.kind;
    setUrlEditor(null);
    ref.current?.focus();
    restoreRange();
    if (url && kind === 'link') {
      const sel = window.getSelection();
      if (sel && sel.isCollapsed) {
        // Nessun testo selezionato: link con l'URL come etichetta.
        document.execCommand(
          'insertHTML',
          false,
          `<a href="${escapeHtml(url)}" target="_blank" rel="noopener noreferrer">${escapeHtml(url)}</a>`,
        );
      } else {
        document.execCommand('createLink', false, url);
      }
    } else if (url && kind === 'image') {
      document.execCommand('insertImage', false, url);
    }
    emit();
  }

  return (
    <div className="rte">
      <div className="rte__toolbar">
        {TOOLS.map(({ cmd, Icon, labelKey, sep }) => (
          <Fragment key={cmd}>
            <button
              type="button"
              className="rte__btn"
              title={t(labelKey)}
              aria-label={t(labelKey)}
              onMouseDown={(e) => runCommand(e, cmd)}
            >
              <Icon fontSize="small" />
            </button>
            {sep && <span className="rte__sep" />}
          </Fragment>
        ))}
        <button
          type="button"
          className="rte__btn"
          title={t('rte.link')}
          aria-label={t('rte.link')}
          onMouseDown={(e) => openUrlEditor('link', e)}
        >
          <InsertLinkIcon fontSize="small" />
        </button>
        <button
          type="button"
          className="rte__btn"
          title={t('rte.quote')}
          aria-label={t('rte.quote')}
          onMouseDown={(e) => runCommand(e, 'formatBlock', 'blockquote')}
        >
          <FormatQuoteIcon fontSize="small" />
        </button>
        <span className="rte__sep" />
        <button
          type="button"
          className="rte__btn"
          title={t('rte.headings')}
          aria-label={t('rte.headings')}
          onMouseDown={openHeadings}
        >
          <TitleIcon fontSize="small" />
        </button>
        <button
          type="button"
          className="rte__btn"
          title={t('rte.textColor')}
          aria-label={t('rte.textColor')}
          onMouseDown={openColorPicker}
        >
          <FormatColorTextIcon fontSize="small" />
        </button>
        <span className="rte__sep" />
        {/* Voci inseribili, in coda agli strumenti che scrivono e dietro un separatore: non
            formatta ciò che c'è, aggiunge contenuto, ed è l'unico pulsante che non c'è sempre.
            DUE pulsanti sulla stessa lista: il primo scrive il solo valore, il secondo lo fa
            precedere dall'etichetta e va a capo. Due e non una voce doppia nel menu perché la
            scelta "col nome davanti" è la stessa per tutte le voci e si prende una volta, mentre
            un menu di lunghezza doppia la farebbe ripetere a ogni riga. */}
        {insertables && insertables.length > 0 && (
          <>
            <button
              type="button"
              className="rte__btn"
              title={insertablesLabel ?? t('rte.insert')}
              aria-label={insertablesLabel ?? t('rte.insert')}
              onMouseDown={(e) => openInsertables(e, false)}
            >
              <DataObjectIcon fontSize="small" />
            </button>
            <button
              type="button"
              className="rte__btn"
              title={t('rte.insertLabeled')}
              aria-label={t('rte.insertLabeled')}
              onMouseDown={(e) => openInsertables(e, true)}
            >
              <LabelIcon fontSize="small" />
            </button>
            <span className="rte__sep" />
          </>
        )}
        {/* Cancella formattazione ULTIMO, sempre: è l'unico che disfa invece di fare, e il posto
            in fondo lo tiene lontano dagli strumenti che si usano di seguito mentre si scrive. Un
            pulsante nuovo va aggiunto PRIMA di questo. */}
        <button
          type="button"
          className="rte__btn"
          title={t('rte.clearFormat')}
          aria-label={t('rte.clearFormat')}
          onMouseDown={clearFormatting}
        >
          <FormatClearIcon fontSize="small" />
        </button>
        {/* Immagini disabilitate momentaneamente (riabilitare il bottone +
            import ImageOutlinedIcon quando servirà). */}
      </div>
      <div
        ref={ref}
        className="rte__area"
        contentEditable={!readOnly}
        suppressContentEditableWarning
        role="textbox"
        aria-multiline="true"
        aria-readonly={readOnly}
        data-placeholder={placeholder}
        onInput={() => {
          emit();
          updateMention();
        }}
        onKeyDown={handleEditorKeyDown}
        onBlur={closeMention}
      />

      {/* Input inline per l'URL di link/immagine, ancorato al bottone. */}
      <Popover
        open={Boolean(urlEditor)}
        anchorEl={urlEditor?.anchorEl ?? null}
        onClose={() => setUrlEditor(null)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
        transformOrigin={{ vertical: 'top', horizontal: 'left' }}
      >
        <div className="rte__url-form">
          <TextField
            autoFocus
            size="small"
            fullWidth
            placeholder={urlEditor?.kind === 'image' ? t('rte.imageUrl') : t('rte.linkUrl')}
            value={urlValue}
            onChange={(e) => setUrlValue(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === 'Enter') {
                e.preventDefault();
                confirmUrl();
              } else if (e.key === 'Escape') {
                e.preventDefault();
                setUrlEditor(null);
              }
            }}
          />
          <Button size="small" variant="contained" onClick={confirmUrl} disabled={!urlValue.trim()}>
            {t('rte.insert')}
          </Button>
        </div>
      </Popover>

      {/* Titoli: stessa lista e stesse regole di focus delle voci inseribili. Le voci restano
          testuali ("Titolo 1"…) perché di un livello di intestazione non esiste icona che si
          capisca — ma qui il testo ha lo spazio che nella toolbar non aveva. */}
      <Popover
        open={Boolean(headingsAnchorEl)}
        anchorEl={headingsAnchorEl}
        onClose={() => setHeadingsAnchorEl(null)}
        disableAutoFocus
        disableEnforceFocus
        anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
        transformOrigin={{ vertical: 'top', horizontal: 'left' }}
      >
        <ul className="rte__mention-list">
          {HEADINGS.map((level) => (
            <li key={level}>
              <button
                type="button"
                className="rte__mention-item"
                onMouseDown={(e) => {
                  e.preventDefault();
                  applyHeading(level);
                }}
              >
                {t('rte.heading', { level })}
              </button>
            </li>
          ))}
        </ul>
      </Popover>

      <ColorPickerPopover
        anchorEl={colorAnchorEl}
        value={textColor}
        onConfirm={applyColor}
        onClose={() => setColorAnchorEl(null)}
      />

      {/* Voci inseribili: stessa lista delle menzioni, e ne riusa le classi perché è la stessa
          cosa a vedersi — un elenco di voci che si scelgono col mouse senza lasciare l'editor.
          disableAuto/EnforceFocus come là: il focus deve restare al contentEditable, o la
          selezione salvata non basterebbe a rimetterci il cursore. */}
      <Popover
        open={Boolean(insertablesAnchorEl)}
        anchorEl={insertablesAnchorEl}
        onClose={() => setInsertablesAnchorEl(null)}
        disableAutoFocus
        disableEnforceFocus
        anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
        transformOrigin={{ vertical: 'top', horizontal: 'left' }}
      >
        <ul className="rte__mention-list">
          {(insertables ?? []).map((item) => (
            <li key={item.value}>
              <button
                type="button"
                className="rte__mention-item"
                onMouseDown={(e) => {
                  e.preventDefault();
                  insertItem(item);
                }}
              >
                {item.label}
              </button>
            </li>
          ))}
        </ul>
      </Popover>

      {/* Lista menzioni (@): ancorata alla posizione del caret. disableAuto/
          EnforceFocus per non rubare il focus al contentEditable. */}
      <Popover
        open={Boolean(mention) && mentionItems.length > 0}
        anchorReference="anchorPosition"
        anchorPosition={mention ?? undefined}
        onClose={closeMention}
        disableAutoFocus
        disableEnforceFocus
        anchorOrigin={{ vertical: 'top', horizontal: 'left' }}
        transformOrigin={{ vertical: 'top', horizontal: 'left' }}
      >
        <ul className="rte__mention-list">
          {mentionItems.map((it, i) => (
            <li key={it.value}>
              <button
                type="button"
                className={`rte__mention-item${i === mentionIndex ? ' is-active' : ''}`}
                onMouseDown={(e) => {
                  e.preventDefault();
                  selectMention(it);
                }}
              >
                {it.label}
              </button>
            </li>
          ))}
        </ul>
      </Popover>
    </div>
  );
}